import React, { useEffect, useState } from "react";
import LayoutWithBreadCrumb from "../../../pages/Pages/Starter/LayoutWithBreadCrumb";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import NumberCommas from "../../../Components/NumberCommas/NumberCommas";
import PrintHeader from "../../../Components/PrintHeader/PrintHeader";
import CounterCashServices from "../../../Services/QRCodeServices/CounterCashServices/CounterCashServices";
import StockSummaryServices from "../../../Services/QRCodeServices/StockSummaryServices/StockSummaryServices";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Select from "react-select";
import { Triangle } from "react-loader-spinner";
import StockItemAdjustment from "./StockItemAdjustment";

const StockItemDetail = () => {
  const [transactionData, setTransactionData] = useState([]);
  const [menuItemName, setMenuItemName] = useState("");
  const { id } = useParams();
  const [itemData, setItemData] = useState([]);
  const [dataS, setDataS] = useState(null); // selected item id
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (id) setDataS(id); // sync dropdown with route param
  }, [id]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };
  const fetchItem = async () => {
    setLoading(true);
    try {
      const response = await StockSummaryServices.getList();
      setItemData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, []);

  async function fetchTransactions() {
    setLoading(true);
    try {
      const response = await StockSummaryServices.getStockItemList(dataS || id); // dropdown has priority
      setMenuItemName(response.menuItemName || "");
      setTransactionData(response.transaction || []);
    } catch (error) {
      toast.error(error.response?.data || "Error fetching transaction details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (dataS || id) {
      fetchTransactions();
    }
  }, [dataS, id]);

  const handlePrint = () => {
    window.print();
  };

  const totalQuantity = transactionData.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const totalPurchase = transactionData.reduce(
    (sum, item) => sum + item.purchaseAmt,
    0
  );
  const totalSales = transactionData.reduce(
    (sum, item) => sum + item.salesAmt,
    0
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const options = itemData.map((supplier) => ({
    value: supplier.menuItemId,
    label: supplier.menuItemName,
  }));

  const handleChange = (selectedOption) => {
    setDataS(selectedOption?.value || null);
  };

  return (
    <React.Fragment>
      <LayoutWithBreadCrumb
        title={`Transactions - ${menuItemName}`}
        pageTitle="Stock Item Transactions"
      >
        <PrintHeader
          reportTitle={`Stock Transaction Report - ${menuItemName}`}
        />

        <Row className="mb-2 d-print-none">
          <Col lg={4}>
            <Select
              value={options.find((option) => option.value === dataS||id) || null}
              onChange={handleChange}
              options={options}
              placeholder="Select item"
              isClearable
            />
          </Col>
          <Col lg={2}>
            <Button
              color="outline-success"
              className="py-2 px-3 d-flex align-items-center shadow-sm ms-auto"
              onClick={toggleModal}
            >
              Item Adjustment
            </Button>
          </Col>
          <Col lg={6} className="d-flex justify-content-end align-items-center">
            <Button
              color="outline-secondary"
              className="py-2 px-3 d-flex align-items-center shadow-sm ms-auto"
              onClick={handlePrint}
            >
              <i className="ri-printer-line fs-16 me-2"></i>
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading ? (
              <div className="my-3 d-flex justify-content-center align-content-center align-items-center">
                <Triangle
                  visible={true}
                  height="80"
                  width="80"
                  color="#5B71B9"
                  ariaLabel="triangle-loading"
                />
              </div>
            ) : (
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th className="table-primary" style={{ width: "60px" }}>
                      SN
                    </th>
                    <th className="table-primary" style={{ width: "200px" }}>
                      Date
                    </th>
                    <th className="table-primary">Remarks</th>
                    <th
                      className="table-primary text-end"
                      style={{ width: "100px" }}
                    >
                      Quantity
                    </th>
                    <th
                      className="table-primary text-end"
                      style={{ width: "150px" }}
                    >
                      Purchase Amt
                    </th>
                    <th
                      className="table-primary text-end"
                      style={{ width: "150px" }}
                    >
                      Sales Amt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactionData.length > 0 ? (
                    transactionData.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{formatDate(item.date)}</td>
                        <td>{item.remarks}</td>
                        <td className="text-end">
                          <NumberCommas number={item.quantity} />
                        </td>
                        <td className="text-end">
                          <NumberCommas number={item.purchaseAmt} />
                        </td>
                        <td className="text-end">
                          <NumberCommas number={item.salesAmt} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No transactions available
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ textAlign: "right" }}>
                    <td colSpan="3" className="fw-bold">
                      Total
                    </td>
                    <td className="fw-bold text-end">
                      <NumberCommas number={totalQuantity} />
                    </td>
                    <td className="fw-bold text-end">
                      <NumberCommas number={totalPurchase} />
                    </td>
                    <td className="fw-bold text-end">
                      <NumberCommas number={totalSales} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardBody>
        </Card>
      </LayoutWithBreadCrumb>
      <StockItemAdjustment
        isOpen={isModalOpen}
        toggle={toggleModal}
        dataS={dataS ? dataS : id}
      />
    </React.Fragment>
  );
};

export default StockItemDetail;
